/**
 * Every contract for patient migration lives here, so if anything moves exactly one file
 * changes.
 *
 * Export responds with a ZIP, not JSON: manifest.json, bundle.json and csv/<class>.csv. Only
 * bundle.json is ever read back by the importer; the CSVs are for people.
 */

import { PatientTabs } from 'constants/enums';

export const MIGRATION_BASE = '/clinician-gateway/migration';

/**
 * The server's cap on one export. Enforced in the UI as well so the operator is stopped before
 * the request rather than by a refusal afterwards, and because select-all-in-group selects a
 * count this screen does not know.
 */
export const MIGRATION_EXPORT_MAX_PATIENTS = 50;

export const migrationRoutes = {
  exportPatients: `${MIGRATION_BASE}/export`,
  importDryRun: `${MIGRATION_BASE}/import/dry-run`,
  importCommit: `${MIGRATION_BASE}/import`,
  /** Destination. Returns the receipt bytes directly; there is no separate receipt route. */
  registerDevices: (uid: string) => `${MIGRATION_BASE}/patients/${uid}/devices/register`,
  /** Source, irreversible. Multipart receipt upload. This IS "Complete migration". */
  releaseDevices: `${MIGRATION_BASE}/devices/release`,
  cancelMigration: (uid: string) => `${MIGRATION_BASE}/patients/${uid}/cancel`,
  status: (uid: string) => `${MIGRATION_BASE}/patients/${uid}/status`
};

/** One operator, one package: a larger selection is a cohort move, which V1 does not support. */

/**
 * destinationEnvironment is required on a move and absent on a backup. A move marks the source
 * and arms the archival guard; a backup must leave the patient untouched. Expressed as a union
 * so a mis-set intent is a compile error rather than something discovered months later when
 * someone cannot archive a patient.
 */
export type IExportRequest =
  | { uids: string[]; intent: 'backup' }
  | { uids: string[]; intent: 'move'; destinationEnvironment: string };

/**
 * Fails closed: only an explicit 'warning' severity is non-blocking, so an absent or unrecognised
 * one blocks. Wrong the other way, it performs an import nobody approved.
 */
export const isBlockingConflict = (conflict: IMigrationConflict) => conflict.severity !== 'warning';

export const hasBlockingConflict = (conflicts: IMigrationConflict[] | undefined) =>
  (conflicts ?? []).some(isBlockingConflict);

/**
 * The server's verdict on whether this package can be imported at all, not a count. Absent means
 * an older server than this build expects, which is treated as a refusal rather than a yes.
 */
export const isPackageImportable = (dryRun: IMigrationDryRun | null | undefined) =>
  dryRun?.importable === true;

export const conflictLabelKey = (kind: IMigrationConflictKind) => `migrationConflict.${kind}`;

/**
 * Parses the receipt for display only. The caller keeps the original bytes to upload: the
 * signature is over `payload` exactly as sent, so a re-serialised copy fails verification at
 * release, and that failure reads as a bad key rather than a mangled file.
 */
export const parseReceiptPayload = (file: unknown): IMigrationReceiptPayload | null => {
  const payload = (file as IMigrationReceiptFile | null)?.payload;
  if (typeof payload !== 'string') return null;
  try {
    return JSON.parse(payload) as IMigrationReceiptPayload;
  } catch {
    return null;
  }
};

/**
 * Per-device detail rides in response headers rather than the body, because the body is the
 * receipt file. Absent headers are normal: nothing deferred means no reasons header at all.
 */
export const readRegisterSummary = (headers: unknown): IMigrationRegisterSummary => {
  const read = (name: string) => (headers as Record<string, string> | undefined)?.[name];
  const toCount = (value: string | undefined) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  };

  let reasons: IMigrationDeferredDevice[] = [];
  try {
    const raw = read('x-migration-deferred-reasons');
    const parsed = raw ? JSON.parse(raw) : [];
    if (Array.isArray(parsed)) reasons = parsed as IMigrationDeferredDevice[];
  } catch {
    // A malformed header must not cost the operator the receipt it accompanies.
    reasons = [];
  }

  return {
    registered: toCount(read('x-migration-registered')),
    deferred: toCount(read('x-migration-deferred')),
    reasons
  };
};

/**
 * The 422 body is JSON, but the request asked for a blob, so it arrives as one and has to be
 * read back as text before it means anything.
 */
/**
 * A JSON error body arrives as a Blob whenever the request asked for one, which every download
 * here does, so `getApiErrorMessage` would find a Blob where it expects `message` and fall back
 * to the generic sentence. This reads it back in place, leaving the error otherwise untouched so
 * axios still recognises it.
 */
export const readBlobError = async (error: unknown) => {
  const response = (error as { response?: { data?: unknown } })?.response;
  if (!(response?.data instanceof Blob)) return error;
  try {
    response.data = JSON.parse(await response.data.text());
  } catch {
    // Not JSON: leave it, and the caller's fallback sentence stands.
  }
  return error;
};

/** 4xx means the server has decided something; a retry of the same request will decide it again. */
export const isRefusal = (error: unknown) => {
  const status = (error as { response?: { status?: number } })?.response?.status;
  return !!status && status >= 400 && status < 500;
};

/**
 * Export answers with a zip, so its 422 arrives as a Blob and has to be read back before it says
 * anything. Returns null for anything that is not that refusal, so the caller can fall through to
 * the generic message.
 */
export const readExportFailure = async (
  error: unknown
): Promise<IMigrationExportFailure | null> => {
  const read = (await readBlobError(error)) as { response?: { status?: number; data?: unknown } };
  if (read?.response?.status !== 422) return null;
  const data = read.response.data as IMigrationExportFailure | undefined;
  return data?.message ? data : null;
};

export const readRegisterFailure = async (
  error: unknown
): Promise<IMigrationRegisterFailure | null> => {
  const response = (error as { response?: { status?: number; data?: unknown } })?.response;
  if (response?.status !== 422) return null;

  try {
    const data = response.data;
    const text = data instanceof Blob ? await data.text() : JSON.stringify(data);
    return JSON.parse(text) as IMigrationRegisterFailure;
  } catch {
    return null;
  }
};

/**
 * Deliberately not `getPath`, which builds its gid from the portal's current selection. An
 * imported patient lives in the group the operator chose on this screen, which is usually not
 * the one selected in the sidebar, so the path has to carry that gid explicitly. Devices is the
 * tab because registering the devices is the next step of the move.
 */
export const importedPatientPath = (gid: string, uid: string) =>
  `/dashboard/${gid}/patient/${uid}/${PatientTabs.DEVICES}`;

/**
 * Kinds that an `already-imported` patient necessarily also produces: their email is in use and
 * their devices are present precisely BECAUSE they were imported. Listed flat they read as three
 * separate obstacles instead of one fact with two echoes, and on a five-patient package that is
 * fifteen lines saying one thing.
 */
const ALREADY_IMPORTED_ECHOES: IMigrationConflictKind[] = ['email-in-use', 'device-imei-present'];

/**
 * Drops the echoes of an already-imported patient, per patient rather than wholesale: a genuine
 * email clash on a DIFFERENT patient in the same package is still a real obstacle and survives.
 */
export const withoutAlreadyImportedEchoes = (conflicts: IMigrationConflict[] | undefined) => {
  const list = conflicts ?? [];
  const alreadyImportedUids = new Set(
    list
      .filter((conflict) => conflict.kind === 'already-imported')
      .map((conflict) => conflict.sourceUid)
      .filter(Boolean)
  );
  if (!alreadyImportedUids.size) return list;

  return list.filter(
    (conflict) =>
      !(
        ALREADY_IMPORTED_ECHOES.includes(conflict.kind) &&
        alreadyImportedUids.has(conflict.sourceUid)
      )
  );
};

/**
 * Everything the report needs about a conflict list, derived once: separate helpers let the rows
 * and the banner read the filtered and unfiltered lists and disagree.
 *
 * `benignRerun` is true when every blocking conflict is an already-imported patient, which is the
 * successful end state of a re-run rather than an obstacle.
 */
export const summariseConflicts = (conflicts: IMigrationConflict[] | undefined) => {
  const visible = withoutAlreadyImportedEchoes(conflicts);
  const blocking = visible.filter(isBlockingConflict);
  const advisory = visible.filter((conflict) => !isBlockingConflict(conflict));
  const benignRerun =
    blocking.length > 0 && blocking.every((conflict) => conflict.kind === 'already-imported');

  return { visible, blocking, advisory, benignRerun };
};

/**
 * The dry run carries no per-patient "already imported" flag: it reports it as an
 * `already-imported` conflict naming that patient's sourceUid. Reading it from the conflicts is
 * therefore reading what the server said, not a second opinion.
 */
export const isAlreadyImported = (
  patient: IMigrationPatientPreview,
  conflicts: IMigrationConflict[] | undefined
) =>
  (conflicts ?? []).some(
    (conflict) => conflict.kind === 'already-imported' && conflict.sourceUid === patient.sourceUid
  );

/** A package can be part importable and part no-op, so these are counts, not a verdict. */
export const countImportable = (dryRun: IMigrationDryRun | null | undefined) =>
  (dryRun?.patients ?? []).filter((patient) => !isAlreadyImported(patient, dryRun?.conflicts))
    .length;

export const countAlreadyImported = (dryRun: IMigrationDryRun | null | undefined) =>
  (dryRun?.patients ?? []).filter((patient) => isAlreadyImported(patient, dryRun?.conflicts))
    .length;

/**
 * Builds the status from the fields php promotes onto the patient payload, optionally merged
 * with the counts ts serves. Absence of the marker is the normal case for every ordinary
 * patient, so this must never throw or guess.
 */
export const getMigrationStatus = (
  patient: IPatient | null | undefined
): IMigrationStatus | null => {
  const marker = patient?.migrationStatus;
  if (!marker?.destinationEnvironment) return null;

  return {
    destinationEnvironment: marker.destinationEnvironment,
    packageId: marker.packageId,
    exportedAt: patient?.migrationStatusSince || undefined
  };
};

/**
 * The marker exists only for a move, so its presence is the whole check. An absent status reads
 * as "not migrating" rather than as unknown, because the destructive portal actions key off this
 * and must stay available on every ordinary patient.
 */
export const isMigrationOutstanding = (status: IMigrationStatus | null | undefined) => !!status;

/**
 * The receipt is an opaque file. Its signature is over the exact bytes the server sent, so this
 * never parses, reformats or re-serialises it: a JSON round trip through the browser invalidates
 * it, and the failure at release reads as a bad key rather than a mangled file.
 */
export const downloadReceipt = (body: Blob, sourceUid: string) =>
  downloadOpaqueFile(body, `migration-receipt-${sourceUid}.json`);

export const downloadPackage = (body: Blob, packageName: string) =>
  downloadOpaqueFile(body, packageName);

const downloadOpaqueFile = (body: Blob, filename: string) => {
  const url = URL.createObjectURL(body);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};
